import React from "react";
import { Heading, If, makeClassName, Modal, ModalProps, Row, toLocalDateString } from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { Contract, useContractDetailQuery } from "../../../../Utilities";
import { ContractFormat } from "../../../Format";
import { PaymentForm } from "../../../Forms";
import { PaymentLineItemFormValues } from "../../../FormUtilities";
import { ContractUtilities } from "../../../Utilities";
import { PaymentCreateModalController } from "../../Payment";
import { ContractDetailDisplay, DetailDisplayContract } from "../DetailDisplay";
import styles from "./styles.module.css";

export type ControlledContractModalProps = Omit<ContractModalProps, "isLoading" | "contract"> & {
	contractID: Contract["id"];
};

export function ControlledContractModal({ contractID, ...props }: ControlledContractModalProps) {
	const { loading, data } = useContractDetailQuery({ variables: { id: contractID } });
	const contract = data?.contract;

	return <ContractModal {...props} isLoading={loading} contract={contract} />;
}

export type ModalContract = DetailDisplayContract;

export type ContractModalProps = {
	variant: "admin" | "parent";
	isLoading?: boolean;
	contract: ModalContract | null | undefined;
	onPaymentCreateSuccess?: () => void;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function ContractModal({
	variant,
	isLoading = false,
	contract,
	onPaymentCreateSuccess,
	ifRef,
	onClose,
	className,
	style,
}: ContractModalProps) {
	const anyOutstandingBalance = React.useMemo(() => {
		if (!contract) {
			return false;
		}
		const precomputedContractInstallments = ContractUtilities.precomputeContractInstallments(contract);
		return precomputedContractInstallments.some((e) => e.amountDue > 0);
	}, [contract]);

	const paymentCreateFormStateArgs = React.useMemo<PaymentForm.ControlledCreateProps["formStateArgs"]>(() => {
		return {
			initialFormValues: {
				issueDate: toLocalDateString(new Date()),
				paymentLineItems: [PaymentLineItemFormValues.blankItemInPayment],
			},
		};
	}, []);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>{contract ? ContractFormat.name(contract) : "Contract"}</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<ContractDetailDisplay variant={variant} isLoading={isLoading} contract={contract} />
			</Modal.Body>
			{contract && (
				<If
					condition={
						variant === "admin" && anyOutstandingBalance && contract.status === ContractUtilities.ContractStatus.Active
					}
				>
					<Modal.Footer className={styles["modal__footer"]}>
						<Row justify="flex-end">
							<PaymentCreateModalController
								variant="primary"
								studentID={contract.student.id}
								contractID={contract.id}
								formStateArgs={paymentCreateFormStateArgs}
								onSuccess={onPaymentCreateSuccess}
							>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
									<Icon.PlusCircle size={20} /> Record Payment
								</Row>
							</PaymentCreateModalController>
						</Row>
					</Modal.Footer>
				</If>
			)}
		</Modal>
	);
}
